/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import StringResources from './nls/StringResources';
import { Label } from '@ba/ba-ui-carbon-toolkit';
import FieldDropdown from './FieldDropdown';
import { observer } from 'mobx-react';
import { isAlive } from 'mobx-state-tree';
import $ from 'jquery';
import { getDataSourceIdTypeIn } from './utils';
import '../stylesheets/slotItemPlaceholder.scss';
const MAX_HEIGHT = 500;

class SlotItemPlaceholder extends Component {

	constructor(props) {
		super(props);
		this.state = {
			isTypeInMode: false,
			showDropzone: false
		};
		this.placeholderRef = React.createRef();
	}

	componentDidMount() {
		const options = {
			onDragEnter: this._placeholderDropzoneOn.bind(this),
			onDragLeave: this._placeholderDropzoneOff.bind(this),
			getDndManager: (dndManager) => { this._dndManager = dndManager; }
		};
		this.props.initializeDnd(this.placeholderRef.current, options);
	}

	componentDidUpdate(prevProps, prevState) {
		if(this.state.showDropzone !== prevState.showDropzone) {
			const slotNode = $(this.placeholderRef.current).closest('.slotEditor__slots__slot').get(0);
			// if showDropzone state changes, and if we are dragging now,
			// we call reassessContainer to reassess the drop targets inside `slot`
			if(slotNode && this._dndManager && this._dndManager.dragObject) {
				this._dndManager.reassessContainer(slotNode);
			}
		}
		if (prevProps.slotDataItemHandler !== this.props.slotDataItemHandler) {
			const options = {
				onDragEnter: this._placeholderDropzoneOn.bind(this),
				onDragLeave: this._placeholderDropzoneOff.bind(this),
				getDndManager: (dndManager) => { this._dndManager = dndManager; }
			};
			this.props.initializeDnd(this.placeholderRef.current, options);
		}
	}

	componentWillUnmount() {
		if (this._dndManager) {
			this._dndManager.deregisterDropTarget(this.placeholderRef.current);
		}
	}

	render() {
		return (
			<div className="slotPlaceholder"
				ref={this.placeholderRef}
			>
				<div className="slotPlaceholder__hoverIndicator"></div>
				{this._renderItem()}
			</div>
		);
	}

	_placeholderTypeInOn = () => {
		this.setState({ isTypeInMode: true });
	}

	_placeholderTypeInOff = () => {
		this.setState({ isTypeInMode: false });
	}

	_placeholderDropzoneOn = () => {
		const { slotPlaceholderStateModel } = this.props;
		if (isAlive(slotPlaceholderStateModel)) {
			slotPlaceholderStateModel.setActivePlaceholderExists(true);
		}
		this.setState({ showDropzone: true });
	}

	_placeholderDropzoneOff = () => {
		const { slotPlaceholderStateModel } = this.props;
		if (isAlive(slotPlaceholderStateModel)) {
			slotPlaceholderStateModel.setActivePlaceholderExists(false);
		}
		this.setState({ showDropzone: false });
	}

	/**
	 * @returns {boolean} indicating if placeholder should be rendered
	 * rules to render placeholder:
	 * 1. this slot can accept more items and
	 * there are no other placeholders currently rendered in this slot and
	 * this item is the last one in the slot
	 * 2. state showDropzone is true -> user is dragging metadata item on top of this placeholder
	 */
	shouldRenderPlaceholder() {
		const {
			slotPlaceholderStateModel,
			indexInSlot,
			numberItemsInSlot,
			canAcceptMoreItems
		} = this.props;

		if (!slotPlaceholderStateModel || (!slotPlaceholderStateModel.getActivePlaceholderExists() && canAcceptMoreItems)) {
			return (numberItemsInSlot === (indexInSlot + 1));
		}
		return this.state.showDropzone;
	}

	_renderItem() {
		if (this.shouldRenderPlaceholder()) {
			this.sourceId = this.props.slotDataItemHandler.getDatasourceId() || getDataSourceIdTypeIn(this.props.dashboardApi);
			return (
				<div className="slotPlaceholder__itemPlaceholder"
					tabIndex='0'
					onClick={this._placeholderTypeInOn}
					onKeyUp={ (event) => this.props.a11Y.handleOpenTypeInDropdown(event, this._placeholderTypeInOn.bind(this)) }
				>
					{(this.state.isTypeInMode && this.sourceId) ? this._renderFieldDropdown() :
						<Label label={StringResources.get('placeholderDropzone')} className={'slotPlaceholder__itemPlaceholder__actionLabel'}/>}
				</div>
			);
		}
	}

	_renderFieldDropdown() {
		return (
			<FieldDropdown
				a11Y={this.props.a11Y}
				open={this.state.isTypeInMode}
				dashboardApi={this.props.dashboardApi}
				getDropdownMaxHeight={this.getDropdownMaxHeight}
				onBlur={this._placeholderTypeInOff}
				slotDataItemHandler={this.props.slotDataItemHandler}
				sourceId={this.sourceId}
				parentNode={this.props.parentNode}
				replaceSlotItem={false}
			/>
		);
	}

	getDropdownMaxHeight() {
		return MAX_HEIGHT;
	}
}

SlotItemPlaceholder.propTypes = {
	a11Y: PropTypes.object.isRequired,
	indexInSlot: PropTypes.number,
	numberItemsInSlot: PropTypes.number,
	canAcceptMoreItems:PropTypes.bool,
	slotDataItemHandler: PropTypes.object.isRequired,
	dashboardApi: PropTypes.object.isRequired,
	initializeDnd: PropTypes.func.isRequired,
	parentNode: PropTypes.node,
	slotPlaceholderStateModel: PropTypes.object
};

SlotItemPlaceholder.defaultProps = {
	indexInSlot: 0,
	numberItemsInSlot: 1,
	slotPlaceholderStateModel: undefined,
	canAcceptMoreItems: true
};


export default observer(SlotItemPlaceholder);



// WEBPACK FOOTER //
// ./src/js/SlotItemPlaceholder.js